import type { ColumnDef } from '@tanstack/react-table';

import { Can } from '@/components/can/Can';
import CrumbTitle from '@/components/crumbTitle/CrumbTitle';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { Button } from '@/design-system/button';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { Translation } from '@/hoslog-intl';
import { DeleteIcon, EditIcon, LockCloseIcon, LockOpenIcon } from '@/icons';
import { ManageCategoryStatusPayload } from '@/services/discount-management/discount-category.mutation';
import { DiscountCategory } from '@/services/discount-management/discount-category.types';
import { Link } from '@tanstack/react-router';
import { ReactNode } from 'react';
import ManageDiscountFormContainer from '../../ManageDiscountFormContainer';

type ModalSize = 'sm' | 'md' | 'lg';
type ModalVariant = 'default' | 'destructive' | 'warning';

type OnOpenProps = <T extends string>(config: {
  title: T;
  modalTitle: string | null;
  okText: string | ReactNode;
  component: ReactNode;
  cancelText?: string | ReactNode;
  size?: ModalSize;
  onCancel?: () => void;
}) => void;

type OpenConfirmationProps = (data: {
  title: string;
  description: string;
  cancelLabel: string;
  actionLabel: string;
  content?: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  onAction: () => Promise<void>;
  onCancel: () => void;
}) => void;

export interface deletePayload {
  categoryDetailId: string;
  categoryId: string;
  status: string;
}

export function getColumns(
  onOpen: OnOpenProps,
  t: (key: string) => string,
  mutateAsync: (payload: ManageCategoryStatusPayload) => Promise<{ message: string }>,
  openConfirmation: OpenConfirmationProps,
): ColumnDef<DiscountCategory>[] {
  return [
    {
      accessorKey: 'sn',
      header: ({ column }) => (
        <DataTableColumnHeader className="whitespace-nowrap" column={column} title={t('common.page.tableColumn.sn')} />
      ),
      cell: ({ row, table }) => (
        <div className="font-medium truncate max-w-[10rem]">{getSerialNumber(table, row.index)}</div>
      ),
      enableSorting: false,
      enableHiding: false,
    },

    {
      accessorKey: 'categoryName',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.categoryType')} />
      ),
      cell: ({ row }) => (
        <Can
          rule={{ allOf: [PERMS.discount.category.detailList] }}
          fallback={<div className="font-medium truncate max-w-[10rem]">{row.getValue('categoryName')}</div>}
        >
          <Link
            to={`/configuration-management/discount-management/$discountCategory`}
            params={{ discountCategory: row.original.categoryId }}
            className="font-medium truncate max-w-[10rem] text-link underline"
          >
            {row.getValue('categoryName')}
          </Link>
        </Can>
      ),
      enableHiding: false,
    },
    {
      accessorKey: 'categoryDescription',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.description')} />
      ),
      cell: ({ row }) => <div>{row.getValue('categoryDescription')}</div>,
      meta: {
        label: 'Description',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.status')} />,
      cell: ({ row }) => {
        const status = row.getValue('status');
        return (
          <div>
            {status === 'A' ? (
              <span className="text-success px-2 py-0.5 bg-success-subtle rounded-2xl">
                <Translation id={'common.page.table.active'} />
              </span>
            ) : (
              <span className="text-destructive-text px-2 py-0.5 bg-destructive-soft/20 rounded-2xl">
                <Translation id={'common.page.table.blocked'} />
              </span>
            )}
          </div>
        );
      },
      meta: {
        label: 'Description',
      },
      enableHiding: false,
    },

    {
      id: 'actions',
      accessorKey: 'actions',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.action')}
        />
      ),
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <div className="flex gap-4 items-center">
          {row.original.status === 'A' ? (
            <Can rule={{ allOf: [PERMS.discount.category.manage] }}>
              <EditIcon
                className="text-foreground-subtle size-4 cursor-pointer"
                onClick={() =>
                  onOpen({
                    title: 'Manage Commission',
                    okText: <CrumbTitle id="common.page.modal.submit" />,
                    size: 'lg',
                    modalTitle: 'configurationManagement.discountManagement.category.manageDiscountCategory.title',
                    component: (
                      <ManageDiscountFormContainer
                        categoryId={`${row.original.categoryId}`}
                        defaultValues={{
                          category: row.getValue('categoryName'),
                          description: row.getValue('categoryDescription'),
                        }}
                      />
                    ),
                  })
                }
              />
            </Can>
          ) : null}

          <Can rule={{ allOf: [PERMS.discount.category.status] }}>
            <Button
              variant={'ghost'}
              className="p-0"
              onClick={() => {
                const isActive = row.original.status === 'A';
                openConfirmation({
                  title: t('common.page.modal.confirmation.title'),
                  description: isActive
                    ? t('common.page.modal.blockDiscountMessage')
                    : t('common.page.modal.unblockDiscountMessage'),
                  cancelLabel: t('common.page.modal.no'),
                  actionLabel: t('common.page.modal.submit'),
                  onAction: async () => {
                    await mutateAsync({
                      categoryId: String(row.original.categoryId),
                      status: row.original.status === 'A' ? 'B' : 'A',
                    });
                  },
                  onCancel: () => {},
                  size: 'lg',
                  variant: 'warning',
                });
              }}
            >
              {row.getValue('status') === 'A' ? (
                <LockCloseIcon className="text-destructive-text size-4 " />
              ) : (
                <LockOpenIcon className="text-link size-4 " />
              )}
            </Button>
          </Can>

          <Can rule={{ allOf: [PERMS.discount.category.manage] }}>
            <DeleteIcon
              className="text-destructive-text size-4 cursor-pointer"
              onClick={() =>
                openConfirmation({
                  title: t('common.page.modal.confirmation.title'),
                  description: t('common.page.modal.deleteDiscountCategoryMessage'),
                  cancelLabel: t('common.page.modal.no'),
                  actionLabel: t('common.page.modal.submit'),
                  onAction: async () => {
                    await mutateAsync({
                      categoryId: String(row.original.categoryId),
                      status: 'D',
                    });
                  },
                  onCancel: () => {},
                  size: 'lg',
                  variant: 'destructive',
                })
              }
            />
          </Can>
        </div>
      ),
      size: 40,
    },
  ];
}
